import {
  deleteV1CompaniesCompanyIdPayCommissionDefaultsId,
  getV1CompaniesCompanyIdPayCommissionDefaults,
  ModelError_Item,
  patchV1CompaniesCompanyIdPayCommissionDefaultsId,
  postV1CompaniesCompanyIdPayCommissionDefaults,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { CommissionDefaultFormData } from '~pages/Settings/RatesManagement/CommissionDefaults/commissionDefaultSchema';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

import { CommissionPayeeType, PayCommissionDefault } from './models';

interface FetchPageParams {
  companyId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  payeeType: CommissionPayeeType;
  search: string;
  signal: AbortSignal;
}

export const isUnprocessable = (
  error: unknown,
): error is AxiosError<{ error?: { errors?: ModelError_Item[] } }> =>
  isAxiosError(error) && error.response?.status === 422;

export default function usePayCommissionDefaults() {
  const fetchPage = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      payeeType,
      search,
      signal,
    }: FetchPageParams) => {
      try {
        const response = await getV1CompaniesCompanyIdPayCommissionDefaults({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit,
            payee_type: payeeType,
            ...(search ? { 'search[query]': search } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });
        return {
          data: response.data.data.map(PayCommissionDefault.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.pay_commission_defaults.fetch_failed'),
        );
      }
    },
    [],
  );

  const create = useCallback(
    async (companyId: string, formData: CommissionDefaultFormData, isDriver: boolean) => {
      try {
        const response = await postV1CompaniesCompanyIdPayCommissionDefaults({
          path: { 'company-id': companyId },
          body: PayCommissionDefault.deparseCreate(formData, isDriver),
        });
        return PayCommissionDefault.parse(response.data.data);
      } catch (error) {
        if (isUnprocessable(error)) throw error;
        return connection.handleRequestError(
          error,
          t('error_messages.pay_commission_defaults.save_failed'),
        );
      }
    },
    [],
  );

  const update = useCallback(
    async (companyId: string, id: string, formData: CommissionDefaultFormData) => {
      try {
        const response = await patchV1CompaniesCompanyIdPayCommissionDefaultsId({
          path: { 'company-id': companyId, id },
          body: PayCommissionDefault.deparseUpdate(formData),
        });
        return PayCommissionDefault.parse(response.data.data);
      } catch (error) {
        if (isUnprocessable(error)) throw error;
        return connection.handleRequestError(
          error,
          t('error_messages.pay_commission_defaults.save_failed'),
        );
      }
    },
    [],
  );

  const destroy = useCallback(async (companyId: string, id: string) => {
    try {
      await deleteV1CompaniesCompanyIdPayCommissionDefaultsId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      return connection.handleRequestError(
        error,
        t('error_messages.pay_commission_defaults.delete_failed'),
      );
    }
  }, []);

  return { create, destroy, fetchPage, update };
}
